/**
 * The note that lifts off the form and flies into the Diary link in the sidebar.
 *
 * Built by hand on the page (not in React) because it must outlive the form that launched it, which closes the moment
 * the note leaves. Skipped for anyone who asked their system for less motion. Falls back to the top right of the
 * screen when the Diary link is not on the page (a collapsed sidebar, a phone).
 */
export function flyNoteToDiary(from: DOMRect, text: string): void {
  if (typeof document === 'undefined') return
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  const target = document.querySelector('[data-diary-nav]')?.getBoundingClientRect()
  const endX = target ? target.left + target.width / 2 : window.innerWidth - 48
  const endY = target ? target.top + target.height / 2 : 36

  const note = document.createElement('div')
  note.setAttribute('aria-hidden', 'true')
  note.textContent = text.slice(0, 90) || 'Today'
  Object.assign(note.style, {
    position: 'fixed',
    zIndex: '2000',
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    boxSizing: 'border-box',
    padding: '18px',
    borderRadius: '10px',
    background: 'var(--color-card)',
    border: '2px solid var(--color-warning)',
    color: 'var(--color-foreground)',
    boxShadow: 'var(--shadow-lg)',
    font: '600 15px/1.35 var(--font-sans, system-ui)',
    overflow: 'hidden',
    pointerEvents: 'none',
    transformOrigin: 'center',
  } satisfies Partial<CSSStyleDeclaration>)
  document.body.appendChild(note)

  const dx = endX - (from.left + from.width / 2)
  const dy = endY - (from.top + from.height / 2)
  const flight = note.animate(
    [
      { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
      { transform: 'translate(0,-24px) scale(0.55) rotate(-4deg)', opacity: 1, offset: 0.25 },
      { transform: `translate(${dx * 0.8}px, ${dy * 0.8 - 40}px) scale(0.2) rotate(8deg)`, opacity: 1, offset: 0.75 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.03) rotate(14deg)`, opacity: 0.2, offset: 1 },
    ],
    { duration: 1100, easing: 'cubic-bezier(.5,.05,.3,1)', fill: 'forwards' },
  )
  flight.onfinish = () => {
    note.remove()
    const link = document.querySelector('[data-diary-nav]')
    link?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(0.95)' }, { transform: 'scale(1)' }],
      { duration: 450, easing: 'ease-out' },
    )
  }
}
